import { Auth } from 'aws-amplify';
import { useContext, useEffect, useMemo } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import {
  getCurrentBehaviourForecast,
  getGridCampaigns,
  getHistoricSystemData,
  getTwentyHoursOfSystemData,
  getUserDetails,
  tdasCampaignId,
} from '../api/Optimise';
import { checkSubscriptionExpired, isTrialSubscription } from '../api/Subscription';
import { AppContext } from '../context/AppContext';
import { useActiveSubscription, useInverterConnectionStatus } from '../hooks';
import AppCard from '../ui/AppCard';
import AppLayout from '../ui/Layout/AppLayout';
import DatePickerLayout from '../ui/Layout/DatePickerLayout';
import EnergyFlowSavingLayoutContainer from '../ui/Layout/EnergyFlowSavingLayoutContainer';
import WelcomeModal from '../ui/Modal/WelcomeModal/WelcomeModal';
import { DisconnectAlert, EventAlert } from '../ui/Styled/AlertBarPreset';
import SubscriptionExpiredAlert from '../ui/Styled/AlertBarPreset/SubscriptionExpiredAlert';
import { getUnixTime, subHours } from '../utils/dateUtils';
import { isForecastStale } from '../utils/disconnectHelpers';
import getCampaignById from '../utils/getCampaignById';
import getLoopIsDisabled from '../utils/getLoopIsDisabled';

import {
  AdminContainer,
  EnergyFlowContainer,
  FinancesContainer,
  HelpContainer,
  NotFoundContainer,
  SavingsContainer,
  SettingsContainer,
  TariffRecommenderContainer,
  TodayContainer,
} from './App';

const EnergyFlowSavingWrapper = () => (
  <EnergyFlowSavingLayoutContainer>
    {/* nested routes will render via Outlet inside DatePickerLayout */}
  </EnergyFlowSavingLayoutContainer>
);

const AppContainer = () => {
  const { activeSubscription, fetchedSubscriptions } = useActiveSubscription();
  const navigate = useNavigate();
  const {
    userSub,
    isAdmin,
    setIsAdmin,
    campaigns,
    setCampaigns,
    setTdasCampaign,
    setTdasIsActive,
    gridEvent,
    twentyFourHoursOfData,
    setAnalyticsStartDate,
    showSimulationMode,
    setUserDetails,
    userDetails,
    setTwentyFourHoursOfData,
    setLatestRecord,
    fetchingTwentyFourHoursOfData,
    setFetchingTwentyFourHoursOfData,
    setFetchingBehaviourForecast,
    setForecastIsStaleError,
    isOnboarded,
    subscriptionsDisabled,
  } = useContext(AppContext);

  /**
   * Memoized values
   */
  const { looksDisconnected: inverterDisconnectError, forecastIsStaleError } = useInverterConnectionStatus();
  const loopIsDisabled = useMemo(
    () => getLoopIsDisabled(showSimulationMode, userDetails),
    [showSimulationMode, userDetails]
  );
  const subscriptionHasExpired = useMemo(
    () => checkSubscriptionExpired(activeSubscription?.subscription_expiry),
    [activeSubscription?.subscription_expiry]
  );
  const showSubscriptionExpiredAlert = useMemo(
    () => !subscriptionsDisabled && subscriptionHasExpired,
    [subscriptionsDisabled, subscriptionHasExpired]
  );

  // Only compute/emit the trial flag once subscriptions have been fetched,
  // so we can default to hiding the pre-grace banner (prevents flashing).
  const isTrial = useMemo(
    () => (fetchedSubscriptions ? isTrialSubscription(activeSubscription) : undefined),
    [fetchedSubscriptions, activeSubscription]
  );

  /**
   * Side effects
   */
  useEffect(() => {
    if (!isOnboarded) {
      navigate('/onboarding', { replace: true });
    }
  }, [isOnboarded, navigate]);

  /**
   * Check if the user has a recent behaviour forecast and show an error banner if not
   */
  useEffect(() => {
    const fetchBehaviourForecast = async () => {
      setFetchingBehaviourForecast(true);
      try {
        const forecast = await getCurrentBehaviourForecast(userSub);
        setForecastIsStaleError(isForecastStale(forecast));
      } catch (error) {
        console.error('Error fetching current behaviour forecast:', error);
        setForecastIsStaleError(false);
      }
      setFetchingBehaviourForecast(false);
    };
    fetchBehaviourForecast();
  }, [userSub, setFetchingBehaviourForecast, setForecastIsStaleError]);

  /**
   * System data retrieval and processing
   */
  useEffect(() => {
    const asyncUseEffect = async () => {
      setFetchingTwentyFourHoursOfData(true);
      try {
        let data = [];
        if (!loopIsDisabled) {
          data = await getTwentyHoursOfSystemData(userSub).catch((error) =>
            console.error('Error fetching historic system data:', error)
          );
        } else {
          const now = getUnixTime(new Date());
          const twentyFourHoursAgo = getUnixTime(subHours(new Date(), 24));
          data = await getHistoricSystemData(twentyFourHoursAgo, now, userSub).catch((error) =>
            console.error('Error fetching historic system data:', error)
          );
        }
        if (!!data) {
          const latestRecord = data[data?.length - 1];
          setLatestRecord(latestRecord);
        }
        setTwentyFourHoursOfData(data);
        setFetchingTwentyFourHoursOfData(false);
      } catch (error) {
        setFetchingTwentyFourHoursOfData(false);
        console.error(error);
      }

      try {
        const user = await getUserDetails(userSub);
        setUserDetails(user);
        if (user?.plant_data_range.start_datetime) {
          setAnalyticsStartDate(user?.plant_data_range.start_datetime);
        } else {
          setAnalyticsStartDate(null);
        }
      } catch (error) {
        console.error(error);
        setAnalyticsStartDate(null);
      }
    };
    asyncUseEffect();
  }, [
    loopIsDisabled,
    setAnalyticsStartDate,
    setFetchingTwentyFourHoursOfData,
    setLatestRecord,
    setTwentyFourHoursOfData,
    setUserDetails,
    userSub,
  ]);

  /**
   * Check if the user is an admin
   */
  useEffect(() => {
    const asyncUseEffect = async () => {
      const user = await Auth.currentAuthenticatedUser();
      if (!!user?.attributes && 'custom:admin_secret' in user.attributes) {
        setIsAdmin(true);
        if (!userSub) {
          navigate('/app/admin');
        }
      }
    };
    asyncUseEffect();
  }, [navigate, setIsAdmin, userSub]);

  /**
   * Grab all TDAS campaigns
   */
  useEffect(() => {
    getGridCampaigns()
      .then((campaigns) => setCampaigns(campaigns))
      .catch((error) => console.error(error));
  }, [setCampaigns]);

  /**
   * Check if turn down and save campaign is active
   */
  useEffect(() => {
    if (campaigns?.length) {
      const tdasCampaign = getCampaignById(campaigns, tdasCampaignId);
      setTdasCampaign(tdasCampaign);
      setTdasIsActive(tdasCampaign?.is_active);
    }
  }, [campaigns, setTdasCampaign, setTdasIsActive]);

  return (
    <>
      {!isAdmin && showSubscriptionExpiredAlert && (
        <SubscriptionExpiredAlert
          subscriptionExpiry={activeSubscription?.subscription_expiry}
          renewalDate={activeSubscription?.renewal_date}
          isTrial={isTrial}
        />
      )}
      {!showSubscriptionExpiredAlert && inverterDisconnectError && (
        <DisconnectAlert forecastIsStaleError={forecastIsStaleError} />
      )}
      {gridEvent && <EventAlert gridEvent={gridEvent} />}
      <WelcomeModal />
      <AppLayout hideSupport>
        <Routes>
          {isAdmin && <Route path="admin" element={<AdminContainer />} />}

          <Route path="/*" element={<AppCard />}>
            <Route path="today" element={<TodayContainer />} />
            <Route path="savings" element={<EnergyFlowSavingWrapper />}>
              <Route element={<DatePickerLayout />}>
                <Route index element={<SavingsContainer />} />
                <Route path=":timestamp" element={<SavingsContainer />} />
              </Route>
            </Route>

            <Route path="finances" element={<EnergyFlowSavingWrapper />}>
              <Route index element={<FinancesContainer />} />
            </Route>

            <Route path="energy-flow" element={<EnergyFlowSavingWrapper />}>
              <Route element={<DatePickerLayout />}>
                <Route index element={<EnergyFlowContainer />} />
                <Route path=":timestamp" element={<EnergyFlowContainer />} />
              </Route>
            </Route>

            <Route path="tariffs/*" element={<TariffRecommenderContainer />} />
            <Route path="settings" element={<SettingsContainer />} />
            <Route path="help" element={<HelpContainer />} />
            <Route path="*" element={<NotFoundContainer />} />
          </Route>

          <Route index element={<Navigate to="today" replace />} />
        </Routes>
      </AppLayout>
    </>
  );
};

export default AppContainer;
